import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
} from '@mui/material';
import {
  Pause as PauseIcon,
  PlayArrow as ResumeIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useAccessControl } from '../../../hooks/useAccessControl';
import { RESOURCES, ACTIONS } from '../../../hooks/usePermissions';
import { ledgerAPI } from '../../../services/api';
import { formatAccountingAmount } from '../../../utils/formatters';
import { useAppSnackbar, useConfirmDialog } from '../../common/TableDesign';
import LedgerListPage from '../LedgerListPage';
import { AccountPicker } from '../LedgerPickers';
import { IncomeStatusChip, fmtDate, getErrorMessage } from './IncomeShared';

const emptyForm = () => ({
  description: '', amount: '', revenue_account_id: '', deferred_revenue_account_id: '',
  interval_months: 1, start_date: new Date().toISOString().slice(0, 10), end_date: '',
});

// Recurring revenue templates (e.g. a monthly service fee) - generates a
// DRAFT revenue schedule each time it comes due, same run/pause/resume
// idiom as the verified general-ledger/recurring-journals (Journals.js),
// but a distinct, income-specific REST resource with simpler fields.
const RevenueRecurringTab = () => {
  const { can } = useAccessControl();
  const canCreate = can(RESOURCES.LEDGER.INCOME, ACTIONS.CREATE);
  const canUpdate = can(RESOURCES.LEDGER.INCOME, ACTIONS.UPDATE);
  const canDelete = can(RESOURCES.LEDGER.INCOME, ACTIONS.DELETE);
  const { showSnackbar, SnackbarComponent } = useAppSnackbar();
  const { confirm, ConfirmDialogComponent } = useConfirmDialog();

  const [refreshKey, setRefreshKey] = useState(0);
  const [showCreate, setShowCreate] = useState(false);
  const [values, setValues] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);

  const reload = () => setRefreshKey((k) => k + 1);

  const submitCreate = async () => {
    if (!values.description.trim()) {
      showSnackbar('Description is required', 'error');
      return;
    }
    if (!(Number(values.amount) > 0)) {
      showSnackbar('Amount must be greater than zero', 'error');
      return;
    }
    if (!values.revenue_account_id || !values.deferred_revenue_account_id) {
      showSnackbar('Revenue account and deferred revenue account are required', 'error');
      return;
    }
    const payload = {
      description: values.description.trim(),
      amount: Number(values.amount),
      revenue_account_id: Number(values.revenue_account_id),
      deferred_revenue_account_id: Number(values.deferred_revenue_account_id),
      interval_months: Number(values.interval_months) || 1,
      start_date: values.start_date,
    };
    if (values.end_date) payload.end_date = values.end_date;
    try {
      setSaving(true);
      await ledgerAPI.createRevenueRecurring(payload);
      showSnackbar('Recurring revenue template created', 'success');
      setShowCreate(false);
      setValues(emptyForm());
      reload();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };

  const runDue = () => {
    confirm({
      title: 'Run due templates',
      message: 'Generate one DRAFT revenue schedule per due recurring template?',
      onConfirm: async () => {
        try {
          setRunning(true);
          await ledgerAPI.runRevenueRecurring({});
          showSnackbar('Due templates generated as DRAFT schedules', 'success');
          reload();
        } catch (error) {
          showSnackbar(getErrorMessage(error), 'error');
        } finally {
          setRunning(false);
        }
      },
    });
  };

  const togglePause = (r) => {
    const paused = String(r.status).toUpperCase() === 'PAUSED';
    confirm({
      title: paused ? 'Resume template' : 'Pause template',
      message: `${paused ? 'Resume' : 'Pause'} "${r.description}"?`,
      onConfirm: async () => {
        try {
          if (paused) await ledgerAPI.resumeRevenueRecurring(r.recurring_id);
          else await ledgerAPI.pauseRevenueRecurring(r.recurring_id);
          showSnackbar(`Template ${paused ? 'resumed' : 'paused'}`, 'success');
          reload();
        } catch (error) {
          showSnackbar(getErrorMessage(error), 'error');
        }
      },
    });
  };

  return (
    <>
      <LedgerListPage
        resource={RESOURCES.LEDGER.INCOME}
        title="Recurring Revenue"
        subtitle="Templates that generate a DRAFT revenue schedule each time they come due"
        addLabel="New Template"
        idKey="recurring_id"
        searchPlaceholder="Search description..."
        emptyMessage="No recurring revenue templates found."
        refreshKey={refreshKey}
        fetchItems={(params) => ledgerAPI.getRevenueRecurring(params)}
        onAdd={canCreate ? () => { setValues(emptyForm()); setShowCreate(true); } : null}
        extraActions={canCreate ? [{ label: running ? 'Running...' : 'Run Due Now', variant: 'outlined', onClick: runDue }] : []}
        filters={[
          { key: 'status', label: 'Status', options: ['ACTIVE', 'PAUSED', 'COMPLETED'].map((s) => ({ label: s, value: s })) },
        ]}
        columns={[
          { label: 'Description', render: (r) => <strong>{r.description}</strong> },
          { label: 'Amount', render: (r) => formatAccountingAmount(r.amount), align: 'right' },
          { label: 'Every', render: (r) => `${r.interval_months || 1} mo`, hideOnMobile: true },
          { label: 'Next Run', render: (r) => fmtDate(r.next_run_date), hideOnMobile: true },
          { label: 'Status', render: (r) => <IncomeStatusChip status={r.status} /> },
        ]}
        rowActions={(r) => {
          const paused = String(r.status).toUpperCase() === 'PAUSED';
          const actions = [];
          if (canUpdate) {
            actions.push({
              icon: paused ? ResumeIcon : PauseIcon, title: paused ? 'Resume' : 'Pause', color: 'primary',
              onClick: () => togglePause(r),
            });
          }
          if (canDelete) {
            actions.push({
              icon: DeleteIcon, title: 'Delete', color: 'error',
              onClick: () => confirm({
                title: 'Delete template',
                message: `Delete recurring template "${r.description}"? This cannot be undone.`,
                onConfirm: async () => {
                  try {
                    await ledgerAPI.deleteRevenueRecurring(r.recurring_id);
                    showSnackbar('Template deleted', 'success');
                    reload();
                  } catch (error) {
                    showSnackbar(getErrorMessage(error), 'error');
                  }
                },
              }),
            });
          }
          return actions;
        }}
      />

      <Dialog open={showCreate} onClose={() => setShowCreate(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Recurring Revenue Template</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid size={12}>
              <TextField
                fullWidth size="small" label="Description" value={values.description}
                onChange={(e) => setValues((p) => ({ ...p, description: e.target.value }))}
                required
              />
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth size="small" type="number" label="Amount" value={values.amount}
                onChange={(e) => setValues((p) => ({ ...p, amount: e.target.value }))}
                inputProps={{ min: 0.01, step: 'any' }} required
              />
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth size="small" type="number" label="Every (months)" value={values.interval_months}
                onChange={(e) => setValues((p) => ({ ...p, interval_months: e.target.value }))}
                inputProps={{ min: 1, step: 1 }}
              />
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth size="small" type="date" label="Start Date" value={values.start_date}
                onChange={(e) => setValues((p) => ({ ...p, start_date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth size="small" type="date" label="End Date (optional)" value={values.end_date}
                onChange={(e) => setValues((p) => ({ ...p, end_date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={12}>
              <AccountPicker label="Revenue account" value={values.revenue_account_id} onChange={(id) => setValues((p) => ({ ...p, revenue_account_id: id }))} required />
            </Grid>
            <Grid size={12}>
              <AccountPicker label="Deferred revenue account" value={values.deferred_revenue_account_id} onChange={(id) => setValues((p) => ({ ...p, deferred_revenue_account_id: id }))} required />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreate(false)}>Cancel</Button>
          <Button variant="contained" onClick={submitCreate} disabled={saving}>
            {saving ? 'Saving...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {ConfirmDialogComponent}
      {SnackbarComponent}
    </>
  );
};

export default RevenueRecurringTab;
